import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Users, Target, Sparkles, Plus, X, Save, Loader2,
  Heart, Brain, DollarSign, Lightbulb
} from "lucide-react";

export const IdealClientPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [profile, setProfile] = useState({
    demographics: {
      age_range: "",
      gender: "",
      location: "",
      income_level: "",
      occupation: ""
    },
    psychographics: {
      values: "",
      interests: "",
      lifestyle: "",
      personality: ""
    },
    pain_points: [],
    buying_triggers: [],
    desired_outcomes: [],
    ideal_client_statement: ""
  });

  const [newItems, setNewItems] = useState({
    pain_point: "",
    buying_trigger: "",
    desired_outcome: ""
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await authFetch(`${API}/ideal-client`, {  });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setProfile({
            demographics: data.demographics || profile.demographics,
            psychographics: data.psychographics || profile.psychographics,
            pain_points: data.pain_points || [],
            buying_triggers: data.buying_triggers || [],
            desired_outcomes: data.desired_outcomes || [],
            ideal_client_statement: data.ideal_client_statement || ""
          });
        }
      }
    } catch (_error) {
      console.error("Error fetching profile:", _error);
    } finally {
      setLoading(false);
    }
  };

  const addItem = (field, value, newItemKey) => {
    if (value.trim()) {
      setProfile({ ...profile, [field]: [...profile[field], value.trim()] });
      setNewItems({ ...newItems, [newItemKey]: "" });
    }
  };

  const removeItem = (field, index) => {
    setProfile({ ...profile, [field]: profile[field].filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/ideal-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(profile)
      });

      if (response.ok) {
        toast.success("Ideal client profile saved!");
      } else {
        toast.error("Failed to save profile");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateStatement = async () => {
    setGenerating(true);
    try {
      const response = await authFetch(`${API}/ai/generate-ideal-client-statement`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          demographics: profile.demographics,
          psychographics: profile.psychographics,
          pain_points: profile.pain_points,
          buying_triggers: profile.buying_triggers,
          desired_outcomes: profile.desired_outcomes
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({ ...profile, ideal_client_statement: data.statement });
        toast.success("Statement generated!");
      } else {
        toast.error("Failed to generate statement");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="ideal-client-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/dashboard")}
                className="text-neutral-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Ideal Client Profile</h1>
                <p className="text-sm text-neutral-500">Define who you serve best</p>
              </div>
            </div>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-profile-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Profile
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Explanation Card */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">What is the Ideal Client Profile?</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  This tool helps you create a detailed picture of your perfect customer. By defining demographics, 
                  psychographics, pain points, and buying triggers, you'll be able to craft targeted marketing 
                  messages and attract clients who are the best fit for your business.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline" className="bg-white">Demographics & Location</Badge>
                  <Badge variant="outline" className="bg-white">Values & Lifestyle</Badge>
                  <Badge variant="outline" className="bg-white">Pain Points</Badge>
                  <Badge variant="outline" className="bg-white">AI-Generated Statement</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Demographics */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-500" />
              Demographics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Age Range</Label>
                <Select 
                  value={profile.demographics.age_range}
                  onValueChange={(value) => setProfile({
                    ...profile,
                    demographics: { ...profile.demographics, age_range: value }
                  })}
                >
                  <SelectTrigger data-testid="age-range-select">
                    <SelectValue placeholder="Select age range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="18-24">18-24</SelectItem>
                    <SelectItem value="25-34">25-34</SelectItem>
                    <SelectItem value="35-44">35-44</SelectItem>
                    <SelectItem value="45-54">45-54</SelectItem>
                    <SelectItem value="55-64">55-64</SelectItem>
                    <SelectItem value="65+">65+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Gender</Label>
                <Select 
                  value={profile.demographics.gender}
                  onValueChange={(value) => setProfile({
                    ...profile,
                    demographics: { ...profile.demographics, gender: value }
                  })}
                >
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="all">All Genders</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Income Level</Label>
                <Select 
                  value={profile.demographics.income_level}
                  onValueChange={(value) => setProfile({
                    ...profile,
                    demographics: { ...profile.demographics, income_level: value }
                  })}
                >
                  <SelectTrigger data-testid="income-level-select">
                    <SelectValue placeholder="Select income level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-50k">Under $50K</SelectItem>
                    <SelectItem value="50k-100k">$50K - $100K</SelectItem>
                    <SelectItem value="100k-250k">$100K - $250K</SelectItem>
                    <SelectItem value="250k-500k">$250K - $500K</SelectItem>
                    <SelectItem value="500k+">$500K+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={profile.demographics.location}
                  onChange={(e) => setProfile({
                    ...profile,
                    demographics: { ...profile.demographics, location: e.target.value }
                  })}
                  placeholder="e.g., United States, Urban areas"
                  data-testid="location-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Occupation / Industry</Label>
                <Input
                  value={profile.demographics.occupation}
                  onChange={(e) => setProfile({
                    ...profile,
                    demographics: { ...profile.demographics, occupation: e.target.value }
                  })}
                  placeholder="e.g., Business owners, Entrepreneurs, Executives"
                  data-testid="occupation-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Psychographics */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center">
              <Brain className="w-5 h-5 mr-2 text-purple-500" />
              Psychographics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Core Values</Label>
                <Input
                  value={profile.psychographics.values}
                  onChange={(e) => setProfile({
                    ...profile,
                    psychographics: { ...profile.psychographics, values: e.target.value }
                  })}
                  placeholder="e.g., Growth, Family, Freedom, Success"
                  data-testid="values-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Interests</Label>
                <Input
                  value={profile.psychographics.interests}
                  onChange={(e) => setProfile({
                    ...profile,
                    psychographics: { ...profile.psychographics, interests: e.target.value }
                  })}
                  placeholder="e.g., Business development, Leadership, Networking"
                  data-testid="interests-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Lifestyle</Label>
                <Input
                  value={profile.psychographics.lifestyle}
                  onChange={(e) => setProfile({
                    ...profile,
                    psychographics: { ...profile.psychographics, lifestyle: e.target.value }
                  })}
                  placeholder="e.g., Busy professional, Work-life balance seeker"
                  data-testid="lifestyle-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Personality Traits</Label>
                <Input
                  value={profile.psychographics.personality}
                  onChange={(e) => setProfile({
                    ...profile,
                    psychographics: { ...profile.psychographics, personality: e.target.value }
                  })}
                  placeholder="e.g., Ambitious, Coachable, Action-oriented"
                  data-testid="personality-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pain Points, Triggers, Outcomes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pain Points */}
          <Card className="border-neutral-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center">
                <Heart className="w-5 h-5 mr-2 text-red-500" />
                Pain Points
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-3">
                {profile.pain_points.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-red-50 px-3 py-2 rounded-lg text-sm">
                    <span className="text-neutral-700">{item}</span>
                    <button onClick={() => removeItem("pain_points", index)} className="text-neutral-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex space-x-2">
                <Input
                  value={newItems.pain_point}
                  onChange={(e) => setNewItems({ ...newItems, pain_point: e.target.value })}
                  onKeyPress={(e) => e.key === "Enter" && addItem("pain_points", newItems.pain_point, "pain_point")}
                  placeholder="Add pain point..."
                  className="text-sm"
                  data-testid="pain-point-input"
                />
                <Button size="sm" onClick={() => addItem("pain_points", newItems.pain_point, "pain_point")} className="bg-red-500 hover:bg-red-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Buying Triggers */}
          <Card className="border-neutral-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-green-500" />
                Buying Triggers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-3">
                {profile.buying_triggers.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg text-sm">
                    <span className="text-neutral-700">{item}</span>
                    <button onClick={() => removeItem("buying_triggers", index)} className="text-neutral-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex space-x-2">
                <Input
                  value={newItems.buying_trigger}
                  onChange={(e) => setNewItems({ ...newItems, buying_trigger: e.target.value })}
                  onKeyPress={(e) => e.key === "Enter" && addItem("buying_triggers", newItems.buying_trigger, "buying_trigger")}
                  placeholder="Add trigger..."
                  className="text-sm"
                  data-testid="buying-trigger-input"
                />
                <Button size="sm" onClick={() => addItem("buying_triggers", newItems.buying_trigger, "buying_trigger")} className="bg-green-500 hover:bg-green-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Desired Outcomes */}
          <Card className="border-neutral-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-amber-500" />
                Desired Outcomes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-3">
                {profile.desired_outcomes.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-amber-50 px-3 py-2 rounded-lg text-sm">
                    <span className="text-neutral-700">{item}</span>
                    <button onClick={() => removeItem("desired_outcomes", index)} className="text-neutral-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex space-x-2">
                <Input
                  value={newItems.desired_outcome}
                  onChange={(e) => setNewItems({ ...newItems, desired_outcome: e.target.value })}
                  onKeyPress={(e) => e.key === "Enter" && addItem("desired_outcomes", newItems.desired_outcome, "desired_outcome")}
                  placeholder="Add outcome..."
                  className="text-sm"
                  data-testid="desired-outcome-input"
                />
                <Button size="sm" onClick={() => addItem("desired_outcomes", newItems.desired_outcome, "desired_outcome")} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Generated Statement */}
        <Card className="border-emerald-200 bg-emerald-50">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-emerald-600" />
                Ideal Client Statement
              </CardTitle>
              <Button 
                onClick={handleGenerateStatement}
                disabled={generating}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="generate-statement-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={profile.ideal_client_statement}
              onChange={(e) => setProfile({ ...profile, ideal_client_statement: e.target.value })}
              placeholder="Your ideal client statement will appear here after generation, or you can write your own..."
              className="min-h-40 bg-white border-emerald-200"
              data-testid="ideal-client-statement-textarea"
            />
            <p className="text-xs text-emerald-700 mt-2">
              This statement synthesizes your ideal client profile and can guide your marketing and sales efforts.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IdealClientPage;
